import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type { GraphData, GraphNode, GraphEdge, NodeType, EdgeType } from '@the-ledger/shared';

interface GraphState {
    data: GraphData;
    selectedNodeId: string | null;
    selectedEdgeId: string | null;

    // Actions
    addNode: (node: Omit<GraphNode, 'id'>) => string;
    updateNode: (id: string, updates: Partial<GraphNode>) => void;
    removeNode: (id: string) => void;

    addEdge: (edge: Omit<GraphEdge, 'id'>) => string;
    updateEdge: (id: string, updates: Partial<GraphEdge>) => void;
    removeEdge: (id: string) => void;

    selectNode: (id: string | null) => void;
    selectEdge: (id: string | null) => void;

    loadGraph: (data: GraphData) => void;
    clearGraph: () => void;
}

const initialData: GraphData = {
    nodes: [],
    edges: [],
    meta: {
        version: '1.0.0',
        lastUpdated: Date.now(),
    },
};

export const useGraphStore = create<GraphState>((set) => ({
    data: initialData,
    selectedNodeId: null,
    selectedEdgeId: null,

    addNode: (node) => {
        const id = uuidv4();
        set((state) => ({
            data: {
                ...state.data,
                nodes: [...state.data.nodes, { ...node, id, security_checks: node.security_checks || {} }],
                meta: { ...state.data.meta, lastUpdated: Date.now() } as any,
            },
        }));
        return id;
    },

    updateNode: (id, updates) => {
        set((state) => ({
            data: {
                ...state.data,
                nodes: state.data.nodes.map((n) => (n.id === id ? { ...n, ...updates } : n)),
                meta: { ...state.data.meta, lastUpdated: Date.now() } as any,
            },
        }));
    },

    removeNode: (id) => {
        set((state) => ({
            data: {
                ...state.data,
                nodes: state.data.nodes.filter((n) => n.id !== id),
                edges: state.data.edges.filter((e) => e.source !== id && e.target !== id),
                meta: { ...state.data.meta, lastUpdated: Date.now() } as any,
            },
            selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
        }));
    },

    addEdge: (edge) => {
        const id = uuidv4();
        set((state) => ({
            data: {
                ...state.data,
                edges: [...state.data.edges, { ...edge, id }],
                meta: { ...state.data.meta, lastUpdated: Date.now() } as any,
            },
        }));
        return id;
    },

    updateEdge: (id, updates) => {
        set((state) => ({
            data: {
                ...state.data,
                edges: state.data.edges.map((e) => (e.id === id ? { ...e, ...updates } : e)),
                meta: { ...state.data.meta, lastUpdated: Date.now() } as any,
            },
        }));
    },

    removeEdge: (id) => {
        set((state) => ({
            data: {
                ...state.data,
                edges: state.data.edges.filter((e) => e.id !== id),
                meta: { ...state.data.meta, lastUpdated: Date.now() } as any,
            },
            selectedEdgeId: state.selectedEdgeId === id ? null : state.selectedEdgeId,
        }));
    },

    selectNode: (id) => set({ selectedNodeId: id, selectedEdgeId: null }),
    selectEdge: (id) => set({ selectedEdgeId: id, selectedNodeId: null }),

    loadGraph: (data) => set({ data, selectedNodeId: null, selectedEdgeId: null }),
    clearGraph: () => set({ data: initialData, selectedNodeId: null, selectedEdgeId: null }),
}));
